import React, { useState } from 'react';
import { Modal } from '../shared/Modal';
import { Button } from '../shared/Button';
import type { TimerType } from '../../types';

interface CreateTimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, type: TimerType, countdownDurationMs?: number) => void;
}

const PRESETS = [
  { label: '5 min', ms: 5 * 60 * 1000 },
  { label: '10 min', ms: 10 * 60 * 1000 },
  { label: '15 min', ms: 15 * 60 * 1000 },
  { label: '25 min', ms: 25 * 60 * 1000 },
  { label: '45 min', ms: 45 * 60 * 1000 },
  { label: '1h', ms: 60 * 60 * 1000 },
];

export function CreateTimerModal({ isOpen, onClose, onCreate }: CreateTimerModalProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<TimerType>('stopwatch');
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(25);
  const [seconds, setSeconds] = useState(0);

  const handleCreate = () => {
    const timerName = name.trim() || (type === 'stopwatch' ? 'Chronomètre' : 'Countdown');
    if (type === 'countdown') {
      const totalMs = (hours * 3600 + minutes * 60 + seconds) * 1000;
      if (totalMs <= 0) return;
      onCreate(timerName, type, totalMs);
    } else {
      onCreate(timerName, type);
    }
    // Reset form
    setName('');
    setType('stopwatch');
    setHours(0);
    setMinutes(25);
    setSeconds(0);
    onClose();
  };

  const applyPreset = (ms: number) => {
    setHours(Math.floor(ms / 3600000));
    setMinutes(Math.floor((ms % 3600000) / 60000));
    setSeconds(Math.floor((ms % 60000) / 1000));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nouveau Timer">
      <div className="space-y-5">
        {/* Name */}
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-namm-muted mb-2">
            Nom
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sprint 1, Pause café..."
            className="w-full bg-namm-panel-soft border border-namm-line px-4 py-2.5 text-sm font-mono text-namm-text outline-none focus:border-namm-cyan placeholder:text-namm-muted/50"
            maxLength={40}
          />
        </div>

        {/* Type selector */}
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-namm-muted mb-2">
            Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setType('stopwatch')}
              className={`py-2.5 text-xs font-mono uppercase tracking-wider border transition-all ${
                type === 'stopwatch'
                  ? 'border-namm-cyan text-namm-cyan bg-namm-cyan/10'
                  : 'border-namm-line text-namm-muted hover:border-namm-line/80'
              }`}
            >
              ⏱ Chronomètre
            </button>
            <button
              onClick={() => setType('countdown')}
              className={`py-2.5 text-xs font-mono uppercase tracking-wider border transition-all ${
                type === 'countdown'
                  ? 'border-namm-cyan text-namm-cyan bg-namm-cyan/10'
                  : 'border-namm-line text-namm-muted hover:border-namm-line/80'
              }`}
            >
              ⏳ Countdown
            </button>
          </div>
        </div>

        {/* Countdown config */}
        {type === 'countdown' && (
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-namm-muted mb-2">
              Durée
            </label>

            {/* Presets */}
            <div className="flex flex-wrap gap-2 mb-3">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => applyPreset(preset.ms)}
                  className="px-3 py-1 text-[10px] font-mono uppercase border border-namm-line text-namm-muted hover:border-namm-cyan hover:text-namm-cyan transition-all"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Manual input */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[9px] font-mono text-namm-muted mb-1">Heures</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={hours}
                  onChange={(e) => setHours(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full bg-namm-panel-soft border border-namm-line px-3 py-2 text-center text-sm font-mono text-namm-text outline-none focus:border-namm-cyan"
                />
              </div>
              <div>
                <label className="block text-[9px] font-mono text-namm-muted mb-1">Minutes</label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={minutes}
                  onChange={(e) => setMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full bg-namm-panel-soft border border-namm-line px-3 py-2 text-center text-sm font-mono text-namm-text outline-none focus:border-namm-cyan"
                />
              </div>
              <div>
                <label className="block text-[9px] font-mono text-namm-muted mb-1">Secondes</label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={seconds}
                  onChange={(e) => setSeconds(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full bg-namm-panel-soft border border-namm-line px-3 py-2 text-center text-sm font-mono text-namm-text outline-none focus:border-namm-cyan"
                />
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleCreate}>
            Créer
          </Button>
        </div>
      </div>
    </Modal>
  );
}
